/**
 * specs/product-kernel/dashboard-ukrainian-localization.md
 *
 * Locale state as a Zustand store, matching this codebase's own `useRiskStore` convention
 * (store/riskStore.ts) rather than a React Context + Provider — no wrapping needed in App.tsx,
 * and tests can set the active locale directly via `useLocaleStore.setState(...)`, the same
 * pattern every existing *.test.tsx file already uses for `useRiskStore`.
 */

import { create } from 'zustand';
import type { Locale } from './types';

const STORAGE_KEY = 'wowyrd_locale';

function isLocale(value: unknown): value is Locale {
  return value === 'en' || value === 'uk';
}

function defaultLocale(): Locale {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isLocale(stored)) return stored;
  } catch {
    // localStorage can throw (private window, blocked site data) — fall through to the
    // build-time default rather than crash the whole dashboard over a locale preference.
  }
  const buildDefault = import.meta.env.VITE_DEFAULT_LOCALE;
  return isLocale(buildDefault) ? buildDefault : 'en';
}

interface LocaleState {
  locale: Locale;
  setLocale: (locale: Locale) => void;
}

export const useLocaleStore = create<LocaleState>((set) => ({
  locale: defaultLocale(),
  setLocale: (locale) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      // Per-viewer convenience only (same convention as every other localStorage use in this
      // app) — a failed write still updates the in-memory store below, just won't persist
      // across a reload.
    }
    set({ locale });
  },
}));
